import { useState, useRef, useEffect } from 'react';

/**
 * Récupération de données anti-condition-de-course pour la page Reporting Financier.
 *
 * Corrige les chiffres qui « sautent » / se figent sur de mauvaises valeurs quand on
 * enchaîne les filtres : une réponse lente d'un ancien filtre arrivait après celle du
 * nouveau et l'écrasait. Ce hook garantit :
 *   - **Garde anti-périmé** (`reqId`) : seule la réponse de la DERNIÈRE requête est appliquée.
 *   - **Annulation réseau** (AbortController → passé en `signal` à axios) : les requêtes
 *     obsolètes sont réellement coupées (plus de réponses tardives, moins de charge serveur).
 *   - **Debounce** : enchaîner N filtres ne lance qu'UNE requête (par défaut 200 ms).
 *   - **Conservation des données précédentes** : `data` n'est jamais remis à null en cours
 *     de rechargement → aucun flicker (l'appelant affiche l'ancien rendu + un voile « MAJ »).
 *
 * @param {(signal: AbortSignal) => Promise<any>} fetcher  Doit renvoyer l'objet de données
 *        COMPLET (pour un onglet multi-endpoints, faire le Promise.all À L'INTÉRIEUR et
 *        renvoyer un seul objet → un seul setData, atomique).
 * @param {Array} deps  Valeurs primitives des filtres (déclenchent un rechargement).
 * @param {{ debounce?: number, enabled?: boolean }} [opts]
 * @returns {{ data: any, loading: boolean, error: string|null, reload: () => void }}
 */
export default function useReportingData(fetcher, deps, opts = {}) {
  const { debounce = 200, enabled = true } = opts;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reqId = useRef(0);
  const ctrl = useRef(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher; // toujours la dernière closure (filtres à jour)
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const run = () => {
      const myId = ++reqId.current;
      if (ctrl.current) ctrl.current.abort();       // annule la requête précédente
      const controller = new AbortController();
      ctrl.current = controller;
      setLoading(true);
      setError(null); // NB : on NE remet PAS `data` à null → pas de flicker

      Promise.resolve()
        .then(() => fetcherRef.current(controller.signal))
        .then((res) => {
          if (myId !== reqId.current) return; // réponse périmée → ignorée
          setData(res);
          setLoading(false);
        })
        .catch((e) => {
          // Requête annulée : ne rien faire (ni erreur, ni fin de loading).
          if (controller.signal.aborted || e?.name === 'CanceledError' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED') return;
          if (myId !== reqId.current) return;
          setError(e?.response?.data?.error || e?.message || 'Erreur de chargement');
          setLoading(false);
        });
    };

    const handle = setTimeout(run, debounce);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled, reloadTick]);

  // Abort à l'unmount (invalide toute réponse en vol).
  useEffect(() => () => { reqId.current++; if (ctrl.current) ctrl.current.abort(); }, []);

  const reload = () => setReloadTick((t) => t + 1);
  return { data, loading, error, reload };
}
